import { ComponentIds, HierarchyUtils, ProductUtils, ValidationResultFail, ValidationResultType } from '@inter-ikea-core/core';
import { ValidationBase } from '@inter-ikea-core/core-toolbox';
import AdjustableHostComponent from '../components/AdjustableHostComponent';
import { evaluateFrameWithClothRailToTheRight, getFrameToTheLeft, hasCorrectDistanceToNextFrame } from '../systems/FrameDragAdaptAdjustableClothRailSystem';
import { CLOTH_RAIL_ALIGN_SERIES_LEFT, CLOTH_RAIL_RIGHT_FRAME_CONNECTOR_KEY } from '../types';
/**
 * Validation for a frame without an adjustable cloth rail attached on the right side. BUT it might
 * have a frame to the left thats has a cloth rail attached. In that case we need to check that the
 * frame cannot be moved to close or to far away from the frame with the cloth rail.
 */
export class FrameDragValidation extends ValidationBase {
    /**
     * Constructor for FrameDragValidation.
     * @param coreApi - The core API instance.
     */
    constructor(coreApi) {
        super();
        this.name = 'FrameDragValidation';
        this.id = 'FRAME_DRAG_VALIDATION';
        this.evaluateFrame = (frame) => {
            // If I'm not a frame return imediately
            if (!frame.hasComponent(AdjustableHostComponent.id))
                return null;
            // Evaluate my left side
            const leftFrameEvalResult = this.evaluateClothRailOnTheFrameToTheLeftOfMe(frame);
            if (leftFrameEvalResult?.resultType === ValidationResultType.FAIL)
                return leftFrameEvalResult;
            // Evaluate my right side
            const rightFrameEvalResult = this.evaluateClothRailOnMeTowardsTheFrameToTheRight(frame);
            if (rightFrameEvalResult?.resultType === ValidationResultType.FAIL)
                return rightFrameEvalResult;
            return null;
        };
        this.evaluateClothRailOnMeTowardsTheFrameToTheRight = (frame) => {
            return evaluateFrameWithClothRailToTheRight(this.coreApi, frame);
        };
        this.haveClothRailConnectionToTheLeft = (frame) => {
            const alignmentConnections = frame.getComponent(ComponentIds.ALIGNMENT_CONNECTIONS_COMPONENT_ID);
            if (alignmentConnections) {
                const clothRailConnection = alignmentConnections.connections.find(c => c.offeringConnector.key === CLOTH_RAIL_RIGHT_FRAME_CONNECTOR_KEY &&
                    c.demandingConnector.key.includes(CLOTH_RAIL_ALIGN_SERIES_LEFT.slice(0, -1)) // rm trailing -
                );
                if (clothRailConnection) {
                    return HierarchyUtils.parent(clothRailConnection.offeringEntity);
                }
            }
            return null;
        };
        this.evaluateClothRailOnTheFrameToTheLeftOfMe = (frame) => {
            const connectedClothRail = this.haveClothRailConnectionToTheLeft(frame);
            if (connectedClothRail) {
                const getFrameToTheLeftResult = getFrameToTheLeft(this.coreApi, frame);
                if (getFrameToTheLeftResult &&
                    hasCorrectDistanceToNextFrame(getFrameToTheLeftResult.distance, connectedClothRail)) {
                    const alignedClothRailMatchesClothRailToTheLeft = HierarchyUtils.descendants(getFrameToTheLeftResult.frameToTheLeft).some(child => child.id === connectedClothRail.id);
                    if (!alignedClothRailMatchesClothRailToTheLeft) {
                        return new ValidationResultFail('Frame is not aligned with the cloth rail it is supposed to be connected to', ProductUtils.productRefOrEmptyString(frame));
                    }
                }
                else {
                    return new ValidationResultFail('Frame is not aligned with the cloth rail it is supposed to be connected to or have incorrect distance to left frame', ProductUtils.productRefOrEmptyString(frame));
                }
            }
            return null;
        };
        this.coreApi = coreApi;
    }
    validateCurrentState() {
        return null;
    }
    validateSnap(snapActionCandidate) {
        if (!this.isEnabled)
            return null;
        const frame = snapActionCandidate.guestRoot;
        const stackedFrames = this.coreApi.entities.getStackedEntities(frame);
        const allFramesInStack = [frame, ...stackedFrames];
        for (const frame of allFramesInStack) {
            const validationResult = this.evaluateFrame(frame);
            if (validationResult && validationResult.resultType === ValidationResultType.FAIL)
                return validationResult;
        }
        return null;
    }
}
//# sourceMappingURL=FrameDragValidation.js.map